import {
  FiBarChart2,
  FiUsers,
  FiShield,
  FiCreditCard,
  FiStar,
  FiLock,
  FiFlag,
  FiSettings,
  FiMessageSquare,
  FiCalendar,
  FiZap,
  FiPackage,
  FiDollarSign,
  FiRepeat,
  FiGift,
  FiBell,
  FiHelpCircle,
  FiLink,
  FiAlertTriangle,
  FiUserX,
  FiFilm,
  FiMonitor,
  FiFileText,
  FiHeart,
} from 'react-icons/fi';
import React from 'react';
import { AdminRole } from '../types';

export type NavItem = {
  to: string;
  label: string;
  Icon: React.ElementType;
  minRole: AdminRole;
};
export type NavGroup = {
  label: string;
  Icon: React.ElementType;
  minRole: AdminRole;
  paths: string[];
  items: NavItem[];
};

/** The single source of truth for which admin role can reach which page — used to
 * both hide sidebar links and gate route access, so the two can't drift apart. */
export const navGroups: NavGroup[] = [
  {
    label: 'Users',
    Icon: FiUsers,
    minRole: 'MODERATOR',
    paths: ['/users', '/verifications', '/deleted-users', '/kings-and-queens', '/messages', '/matches'],
    items: [
      { to: '/users', label: 'All Users', Icon: FiUsers, minRole: 'MODERATOR' },
      { to: '/verifications', label: 'Verifications', Icon: FiShield, minRole: 'MODERATOR' },
      { to: '/deleted-users', label: 'Deleted Users', Icon: FiUserX, minRole: 'MODERATOR' },
      { to: '/kings-and-queens', label: 'Kings & Queens', Icon: FiStar, minRole: 'MODERATOR' },
      { to: '/messages', label: 'Messages', Icon: FiMessageSquare, minRole: 'MODERATOR' },
      { to: '/matches', label: 'Matches', Icon: FiLink, minRole: 'MODERATOR' },
    ],
  },
  {
    label: 'Content & Safety',
    Icon: FiShield,
    minRole: 'MODERATOR',
    paths: ['/moderation', '/stories', '/support', '/reports', '/blocked-accounts'],
    items: [
      { to: '/moderation', label: 'Moderation', Icon: FiFlag, minRole: 'MODERATOR' },
      { to: '/stories', label: 'Stories', Icon: FiFilm, minRole: 'MODERATOR' },
      { to: '/support', label: 'Support', Icon: FiHelpCircle, minRole: 'MODERATOR' },
      { to: '/reports', label: 'Reports', Icon: FiAlertTriangle, minRole: 'MODERATOR' },
      { to: '/blocked-accounts', label: 'Blocked Accounts', Icon: FiUserX, minRole: 'MODERATOR' },
    ],
  },
  {
    label: 'Payments',
    Icon: FiCreditCard,
    minRole: 'ADMIN',
    paths: ['/plans', '/payments', '/subscriptions', '/roses'],
    items: [
      { to: '/plans', label: 'Plans', Icon: FiPackage, minRole: 'ADMIN' },
      { to: '/payments', label: 'Payments', Icon: FiDollarSign, minRole: 'ADMIN' },
      { to: '/subscriptions', label: 'Subscriptions', Icon: FiRepeat, minRole: 'ADMIN' },
      { to: '/roses', label: 'Roses', Icon: FiGift, minRole: 'ADMIN' },
    ],
  },
  {
    label: 'Platform',
    Icon: FiMonitor,
    minRole: 'EDITOR',
    paths: ['/notifications', '/events', '/blog', '/success-stories', '/logs', '/audit-logs'],
    items: [
      { to: '/notifications', label: 'Notifications', Icon: FiBell, minRole: 'EDITOR' },
      { to: '/events', label: 'Events', Icon: FiCalendar, minRole: 'ADMIN' },
      { to: '/blog', label: 'Blog', Icon: FiFileText, minRole: 'EDITOR' },
      { to: '/success-stories', label: 'Success Stories', Icon: FiHeart, minRole: 'EDITOR' },
      { to: '/logs', label: 'Logs', Icon: FiAlertTriangle, minRole: 'ADMIN' },
      { to: '/audit-logs', label: 'Audit Log', Icon: FiShield, minRole: 'ADMIN' },
    ],
  },
  {
    label: 'Settings',
    Icon: FiSettings,
    minRole: 'ADMIN',
    paths: ['/settings', '/admins'],
    items: [
      { to: '/settings/general', label: 'General', Icon: FiSettings, minRole: 'SUPERADMIN' },
      { to: '/settings/monetization', label: 'Monetization', Icon: FiZap, minRole: 'SUPERADMIN' },
      { to: '/admins', label: 'Admins', Icon: FiLock, minRole: 'ADMIN' },
    ],
  },
];

/** Pages reachable by any authenticated admin (lowest role, EDITOR) that aren't
 * part of a sidebar group — dashboard and the admin's own profile page. */
export const UNGROUPED_ROUTE_ROLES: Record<string, AdminRole> = {
  '/dashboard': 'EDITOR',
  '/admin-profile': 'EDITOR',
};

/** Flat path -> minimum role map, derived from navGroups + ungrouped pages,
 * used to gate route access to match what the sidebar already shows/hides. */
export const ROUTE_MIN_ROLE: Record<string, AdminRole> = {
  ...UNGROUPED_ROUTE_ROLES,
  ...Object.fromEntries(
    navGroups.flatMap((group) => group.items.map((item) => [item.to, item.minRole])),
  ),
};

/** Resolves the minimum role for a path, matching child routes (e.g. /users/:id)
 * against their parent's entry (/users). Defaults to EDITOR for unlisted paths. */
export function getMinRoleForPath(pathname: string): AdminRole {
  if (ROUTE_MIN_ROLE[pathname]) return ROUTE_MIN_ROLE[pathname];
  const match = Object.keys(ROUTE_MIN_ROLE)
    .filter((route) => pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? ROUTE_MIN_ROLE[match] : 'EDITOR';
}
